import { useEffect, useMemo, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { Activity, Archive, BarChart3, Cloud, HardDrive, KeyRound, Palette, Play, RefreshCw, ScrollText, Search, ShieldCheck, ShoppingCart, Users, X } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AMAZON_STORES } from "@/lib/searchLinks";
import { toast } from "sonner";
import { PageHeader } from "@/components/PageHeader";
import { AccountsSection } from "@/components/AccountsSection";
import { InvitesSection } from "@/components/InvitesSection";
import { AuditLog } from "@/components/AuditLog";
import { DangerZone } from "@/components/DangerZone";
import { OfflineSection } from "@/components/OfflineSettings";
import { PlaybackSection } from "@/components/PlaybackSettings";
import { PalettesSection } from "@/components/PalettesSection";
import { SyncSection } from "@/components/SyncSection";
import { BackupSection } from "@/components/BackupSection";
import { SecuritySection } from "@/components/SecuritySection";
import { SecurityDashboard } from "@/components/SecurityDashboard";
import { FileJumpSection } from "@/components/FileJumpSection";
import { OneDriveToggleSection } from "@/components/OneDriveToggle";
import { LibraryHealth } from "@/components/LibraryHealth";
import { VideoDefaultsSection } from "@/components/VideoDefaultsSection";
import Stats from "@/pages/Stats";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";

const AmazonStoreSection = () => {
  const { user, updatePrefs } = useAuth();
  const change = async (id) => {
    try {
      await updatePrefs({ amazon_store: id });
      toast.success(`Amazon links now open ${AMAZON_STORES.find((s) => s.id === id).domain}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <section className="flex flex-wrap items-center justify-between gap-4 rounded-lg border border-zinc-200 bg-white p-8" data-testid="amazon-store-section">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-md bg-amber-50 text-amber-700"><ShoppingCart size={18} /></div>
        <div>
          <h2 className="text-lg font-medium">Amazon store</h2>
          <p className="text-xs text-zinc-500">Used by "Buy on Amazon" and Amazon Music links.</p>
        </div>
      </div>
      <Select value={user?.amazon_store || "com"} onValueChange={change}>
        <SelectTrigger className="w-64" data-testid="amazon-store-select"><SelectValue /></SelectTrigger>
        <SelectContent>
          {AMAZON_STORES.map((s) => (
            <SelectItem key={s.id} value={s.id} data-testid={`amazon-store-option-${s.id}`}>{s.label} <span className="font-mono text-xs text-zinc-400">· {s.domain}</span></SelectItem>
          ))}
        </SelectContent>
      </Select>
    </section>
  );
};

const AccountCard = ({ user, logout }) => (
  <section className="flex items-center justify-between rounded-lg border border-zinc-200 bg-white p-8" data-testid="account-section">
    <div>
      <h2 className="text-lg font-medium">Account</h2>
      <p className="text-sm text-zinc-500">{user?.name} · <span className="font-mono">{user?.email}</span></p>
    </div>
    <button onClick={logout} data-testid="settings-logout-button" className="rounded-full border border-zinc-300 px-5 py-2 text-sm hover:border-zinc-900">Log out</button>
  </section>
);

/* Every settings page: group → page. `show(user)` hides pages the account can't use; `keywords` feed the search box. */
const PAGES = [
  { group: "Personal", id: "playback", label: "Playback", icon: Play, desc: "Gapless, crossfade, equalizer, lyrics and video player defaults.", keywords: "gapless crossfade equalizer eq speed lyrics video aspect ratio buffer theater autoplay",
    render: () => <><PlaybackSection /><VideoDefaultsSection /></> },
  { group: "Personal", id: "appearance", label: "Appearance", icon: Palette, desc: "Themes and colour palettes.", keywords: "theme palette colour color dark light background", show: (u) => u?.themes_enabled !== false,
    render: () => <PalettesSection /> },
  { group: "Personal", id: "sync", label: "Sync & Offline", icon: RefreshCw, desc: "Settings that follow you between devices, and music saved for offline listening.", keywords: "sync devices offline download install app pwa",
    render: () => <><SyncSection /><OfflineSection /></> },
  { group: "Personal", id: "backup", label: "Backup", icon: Archive, desc: "Download playlists, favourites, ratings, play history and progress as a ZIP you can keep anywhere.", keywords: "backup export m3u json playlists favorites ratings history restore nightly filejump",
    render: () => <BackupSection /> },
  { group: "Personal", id: "account", label: "Account & Security", icon: KeyRound, desc: "Two-factor authentication, signed-in devices, log out and account deletion.", keywords: "password two-factor 2fa totp authenticator devices sessions logout delete account email",
    render: ({ user, logout }) => <><SecuritySection /><AccountCard user={user} logout={logout} /><DangerZone /></> },
  { group: "Personal", id: "stats", label: "Listening stats", icon: BarChart3, desc: "Plays, listening time, top artists, albums and genres over time.", keywords: "stats statistics plays history top artists albums genres listening time",
    render: () => <Stats embedded /> },
  { group: "Sources", id: "filejump", label: "FileJump", icon: Cloud, desc: "Your primary cloud storage: connection, music / audiobook / video folders and scans.", keywords: "filejump webdav storage folders scan import audiobooks videos nightly", show: (u) => !!u?.permissions?.filejump,
    render: () => <FileJumpSection /> },
  { group: "Sources", id: "onedrive", label: "OneDrive", icon: HardDrive, desc: "Microsoft OneDrive as an extra music source.", keywords: "onedrive microsoft", show: (u) => !!u?.permissions?.upload_onedrive,
    render: () => <OneDriveToggleSection /> },
  { group: "Sources", id: "stores", label: "Stores", icon: ShoppingCart, desc: "Which Amazon storefront the buy links open.", keywords: "amazon store buy wishlist links",
    render: () => <AmazonStoreSection /> },
  { group: "Administration", owner: true, id: "accounts", label: "Accounts & Invites", icon: Users, desc: "People, roles, approvals, storage limits, reset links and invitations.", keywords: "users guests roles approve invite reset password storage quota signups",
    render: ({ user }) => <><AccountsSection /><InvitesSection senderName={user?.name} /></> },
  { group: "Administration", owner: true, id: "security", label: "Security", icon: ShieldCheck, desc: "Sign-in activity, locked accounts, blocked networks and bot protection.", keywords: "security failed logins lockout block ip turnstile auto-block",
    render: () => <SecurityDashboard /> },
  { group: "Administration", owner: true, id: "health", label: "Library Health", icon: Activity, desc: "Media cache, database footprint and the last scan of each source.", keywords: "cache hit rate database size scan health performance",
    render: () => <LibraryHealth /> },
  { group: "Administration", owner: true, id: "audit", label: "Audit log", icon: ScrollText, desc: "Who changed what, and when.", keywords: "audit log history changes",
    render: () => <AuditLog /> },
];

// Older links: ?tab=admin, #security, #audiobooks, #video-folders
const legacyPage = (params, hash) => {
  if (params.get("tab") === "admin") return "accounts";
  if (hash === "#security") return "account";
  if (hash === "#audiobooks" || hash === "#video-folders") return "filejump";
  return null;
};

export default function Settings() {
  const { user, logout } = useAuth();
  const [params, setParams] = useSearchParams();
  const { hash } = useLocation();
  const [q, setQ] = useState("");
  const [fjStatus, setFjStatus] = useState(null);
  const isOwner = user?.role === "owner";
  const pages = useMemo(() => PAGES.filter((p) => (!p.owner || isOwner) && (!p.show || p.show(user))), [isOwner, user]);
  const wanted = params.get("page") || legacyPage(params, hash);
  const page = pages.find((p) => p.id === wanted) || pages[0];
  const pick = (id) => { setParams({ page: id }, { replace: true }); setQ(""); };
  useEffect(() => { if (user?.permissions?.filejump) api.get("/webdav/status").then((r) => setFjStatus(r.data)).catch(() => {}); }, [user]);
  useEffect(() => {
    if (!hash) return;
    const el = document.querySelector(hash);
    if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 150);
  }, [hash, page.id]);
  const status = (p) => {
    if (p.id === "filejump" && fjStatus) return fjStatus.connected ? "Connected" : "Not connected";
    if (p.id === "account") return user?.totp_enabled ? "2FA on" : "2FA off";
    if (p.id === "appearance") return user?.theme ? user.theme.replace(/[-_]/g, " ") : null;
    return null;
  };
  const needle = q.trim().toLowerCase();
  const hits = needle ? pages.filter((p) => `${p.label} ${p.desc} ${p.keywords}`.toLowerCase().includes(needle)) : [];
  const groups = [...new Set(pages.map((p) => p.group))];
  const Icon = page.icon;
  return (
    <div>
      <PageHeader eyebrow="Settings" title={page.label} subtitle={page.desc} testid="settings-title" />
      <div className="mb-6 lg:hidden">
        <select value={page.id} onChange={(e) => pick(e.target.value)} className="h-10 w-full rounded-md border border-zinc-300 bg-white px-3 text-sm" data-testid="settings-page-select">
          {groups.map((g) => <optgroup key={g} label={g}>{pages.filter((p) => p.group === g).map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}</optgroup>)}
        </select>
      </div>
      <div className="flex flex-col gap-8 lg:flex-row lg:items-start">
        <nav className="hidden w-56 shrink-0 lg:block" data-testid="settings-nav">
          <div className="relative mb-4">
            <Search size={13} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && hits[0]) pick(hits[0].id); if (e.key === "Escape") setQ(""); }}
              placeholder="Search settings" className="h-9 w-full rounded-full border border-zinc-200 bg-white pl-8 pr-8 text-xs outline-none focus:border-zinc-900" data-testid="settings-search" />
            {q && <button onClick={() => setQ("")} className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full p-1 text-zinc-400 hover:text-zinc-900" aria-label="Clear" data-testid="settings-search-clear"><X size={12} /></button>}
          </div>
          {needle ? (
            <div className="space-y-1" data-testid="settings-search-results">
              {hits.length === 0 && <p className="px-3 py-2 text-xs text-zinc-500">No matching settings</p>}
              {hits.map((p) => <NavItem key={p.id} p={p} active={p.id === page.id} onPick={pick} status={status(p)} />)}
            </div>
          ) : groups.map((g) => (
            <div key={g} className="mb-5" data-testid={`settings-group-${g.toLowerCase()}`}>
              <p className="mb-1.5 px-3 text-[10px] font-semibold uppercase tracking-[0.2em] text-zinc-400">{g}</p>
              <div className="space-y-0.5">{pages.filter((p) => p.group === g).map((p) => <NavItem key={p.id} p={p} active={p.id === page.id} onPick={pick} status={status(p)} />)}</div>
            </div>
          ))}
        </nav>
        <div className="min-w-0 flex-1 space-y-6" data-testid={`settings-panel-${page.id}`}>
          <div className="flex items-center gap-2 text-xs text-zinc-500 lg:hidden"><Icon size={13} />{page.group} · {page.label}</div>
          {page.render({ user, logout })}
        </div>
      </div>
    </div>
  );
}

const NavItem = ({ p, active, onPick, status }) => {
  const Icon = p.icon;
  return (
    <button onClick={() => onPick(p.id)} aria-current={active ? "page" : undefined} data-testid={`settings-nav-${p.id}`}
      className={`flex w-full items-center gap-2.5 rounded-md px-3 py-2 text-left text-sm transition-colors ${active ? "bg-zinc-900 text-white" : "text-zinc-700 hover:bg-zinc-100"}`}>
      <Icon size={14} className={active ? "text-white" : "text-zinc-400"} />
      <span className="min-w-0 flex-1 truncate">{p.label}</span>
      {status && <span className={`shrink-0 rounded-full px-1.5 py-0.5 text-[9px] font-medium capitalize ${active ? "bg-white/15 text-white" : "bg-zinc-100 text-zinc-500"}`} data-testid={`settings-status-${p.id}`}>{status}</span>}
    </button>
  );
};
